import { siteImageUrl } from '../../../shared/domain/siteImages.js';

function formatEok(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 2 })}억`;
}

function formatLevel(value) {
  const number = Number(value || 0);
  return number > 0 ? number.toLocaleString('ko-KR', { maximumFractionDigits: 1 }) : '-';
}

function formatCoefficient(value) {
  return Number(value || 1).toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function coefficientRangeLabel(boss = {}) {
  const minimum = Number(boss.coefficientMin ?? boss.coefficient ?? 1);
  const maximum = Number(boss.coefficientMax ?? boss.coefficient ?? 1);
  if (Math.abs(maximum - minimum) < 0.005) return `×${formatCoefficient(boss.coefficient)}`;
  return `×${formatCoefficient(minimum)}~${formatCoefficient(maximum)}`;
}

function deltaLabel(value) {
  if (value === null || value === undefined) return '-';
  const number = Number(value || 0);
  if (!number) return '-';
  return `${number > 0 ? '▲' : '▼'}${Math.abs(number).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}`;
}

export function RaidSettlementBossNikkeUsage({ boss, mobile = false }) {
  const rows = Array.isArray(boss?.mostUsedNikkes) ? boss.mostUsedNikkes : [];
  return (
    <div className={`raid-settlement-boss-nikke-usage ${mobile ? 'mobile' : 'desktop'}`}>
      <div className="raid-settlement-boss-nikke-usage-heading">
        <span>많이 사용한 니케</span>
        <small>{boss?.attackCount || 0}개 실전 덱 기준</small>
      </div>
      {rows.length ? (
        <div className="raid-settlement-boss-nikke-list">
          {rows.map((item, index) => (
            <div className="raid-settlement-boss-nikke-item" key={item.code}>
              <b>{index + 1}</b>
              <span className="raid-settlement-boss-nikke-image">
                {item.imageUrl ? <img src={siteImageUrl(item.imageUrl)} alt="" loading="lazy" /> : <em>{String(item.name || item.code).slice(0, 1)}</em>}
              </span>
              <span className="raid-settlement-boss-nikke-copy"><strong>{item.name || item.code}</strong><small>{item.count}회</small></span>
              <i>{Number(item.usagePercent || 0).toLocaleString('ko-KR', { maximumFractionDigits: 0 })}%</i>
            </div>
          ))}
        </div>
      ) : <p className="raid-settlement-boss-nikke-empty">덱 데이터 없음</p>}
    </div>
  );
}

export function RaidSettlementBossMatrix({ settlement, mobile = false }) {
  const ranking = settlement?.ranking || [];
  const bosses = settlement?.bossTotals || [];
  if (!ranking.length || !bosses.length) return null;

  return (
    <section className={`raid-settlement-boss-matrix ${mobile ? 'mobile' : 'desktop'}`}>
      <header>
        <div><h3>멤버별 보스 딜량</h3><span>보정 기여도 = 각 보스 실제 딜 × 보스 계수의 합계 비율 · 계수는 공격자 20레벨 구간별 적용</span></div>
        <small>가로 스크롤</small>
      </header>
      <div className="raid-settlement-boss-matrix-wrap">
        <table>
          <thead>
            <tr>
              <th>순위</th><th className="member">멤버</th><th>싱크 레벨</th><th>참여</th><th>막타</th><th>보정 기여도</th><th>실제 총딜</th><th>보정 총딜</th>
              {bosses.map((boss) => <th key={boss.bossNo}>{boss.bossName}<small>구간 {coefficientRangeLabel(boss)}</small></th>)}
            </tr>
          </thead>
          <tbody>
            {ranking.map((row) => {
              const level = Math.max(...row.attacks.map((attack) => Number(attack.charLevel || 0)), 0);
              return (
                <tr key={row.memberKey}>
                  <td><b>#{row.rank}</b></td><td className="member"><strong>{row.nickname}</strong></td><td>{level || '-'}</td><td>{row.attackCount}/3</td><td>{row.killCount || '-'}</td>
                  <td><b>{row.contributionPercent.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%</b><small>실제 {row.rawContributionPercent.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%</small></td>
                  <td><strong>{formatEok(row.totalDamageEok)}</strong></td><td><strong>{formatEok(row.weightedDamageEok)}</strong></td>
                  {row.bossDamageRows.map((boss) => (
                    <td className={boss.damageEok > 0 ? 'has-damage' : ''} key={boss.bossNo}>
                      {boss.damageEok > 0 ? <><b>{formatEok(boss.damageEok)}</b><small>→ {formatEok(boss.weightedDamageEok)}</small></> : '-'}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function RaidSettlementGrowthChart({ history, mobile = false }) {
  const seasons = history?.seasonNos || [];
  const rows = (history?.memberRows || []).filter((row) => seasons.some((seasonNo) => Number(row.values?.[seasonNo] || 0) > 0)).slice(0, 24);
  if (!seasons.length || !rows.length) return null;
  const width = 920;
  const height = mobile ? 250 : 300;
  const pad = mobile ? 30 : 38;
  const values = rows.flatMap((row) => seasons.map((seasonNo) => Number(row.values?.[seasonNo] || 0)).filter(Boolean));
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;
  const x = (index) => seasons.length <= 1 ? width / 2 : pad + index * ((width - pad * 2) / (seasons.length - 1));
  const y = (value) => height - pad - ((Number(value) - min) / Math.max(1, max - min)) * (height - pad * 2);

  return (
    <section className={`raid-settlement-growth-chart ${mobile ? 'mobile' : 'desktop'}`}>
      <header><div><h3>유니온 멤버 싱크 레벨 변화량</h3><span>현재 유니온 멤버만 · 각 차수 실전 기록에 저장된 싱크 레벨 기준 · 최대 24명 표시</span></div><small>{seasons[0]}차 ~ {seasons.at(-1)}차</small></header>
      <div>
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="현재 유니온 멤버 차수별 싱크 레벨 성장 그래프">
          {[0, 1, 2, 3, 4].map((index) => <line className="grid" key={index} x1={pad} x2={width - pad} y1={pad + index * ((height - pad * 2) / 4)} y2={pad + index * ((height - pad * 2) / 4)} />)}
          {seasons.map((seasonNo, index) => <text className="axis" key={seasonNo} x={x(index)} y={height - 8} textAnchor="middle">{seasonNo}차</text>)}
          {rows.map((row, rowIndex) => {
            const points = seasons.map((seasonNo, index) => row.values?.[seasonNo] ? `${x(index)},${y(row.values[seasonNo])}` : '').filter(Boolean).join(' ');
            return points ? <polyline className="member" key={row.memberKey} points={points} style={{ '--line-index': rowIndex }}><title>{row.nickname}</title></polyline> : null;
          })}
        </svg>
      </div>
    </section>
  );
}

export function RaidSettlementLevelHistory({ history, errorMessage = '', mobile = false }) {
  const seasonNos = history?.seasonNos || [];
  const memberRows = history?.memberRows || [];
  return (
    <section className={`raid-settlement-level-history ${mobile ? 'mobile' : 'desktop'}`}>
      <header><div><h3>멤버별 싱크로 성장표</h3><span>현재 유니온 멤버만 · 각 차수 실전 기록에 저장된 싱크 레벨 기준</span></div>{history?.current ? <strong>{history.current.seasonNo}차 평균 싱크 {formatLevel(history.current.averageLevel)}</strong> : null}</header>
      {errorMessage ? <p className="raid-settlement-insight-error">{errorMessage}</p> : null}
      {!seasonNos.length || !memberRows.length ? <p className="raid-settlement-insight-empty">싱크 레벨이 포함된 실전 기록이 없습니다.</p> : (
        <><div className="raid-settlement-growth-summary"><div><span>직전 차수</span><strong>{history.previous ? `${history.previous.seasonNo}차` : '없음'}</strong></div><div><span>평균 상승</span><strong className={Number(history.averageDelta || 0) >= 0 ? 'up' : 'down'}>{deltaLabel(history.averageDelta)}</strong></div><div><span>현재 최고</span><strong>{formatLevel(history.current?.maxLevel)}</strong></div></div>
          <div className="raid-settlement-growth-table-wrap"><table className="raid-settlement-growth-table"><thead><tr><th className="rank">NO.</th><th className="member">닉네임</th>{seasonNos.map((seasonNo) => <th className={seasonNo === history.current?.seasonNo ? 'current' : ''} key={seasonNo}>{seasonNo}차</th>)}<th>상승량</th></tr></thead><tbody>{memberRows.map((member, index) => <tr key={member.memberKey}><td className="rank">{index + 1}</td><td className="member"><strong>{member.nickname}</strong></td>{seasonNos.map((seasonNo) => <td className={seasonNo === history.current?.seasonNo ? 'current' : ''} key={seasonNo}>{formatLevel(member.values?.[seasonNo])}</td>)}<td><b className={Number(member.delta || 0) > 0 ? 'up' : Number(member.delta || 0) < 0 ? 'down' : ''}>{deltaLabel(member.delta)}</b></td></tr>)}</tbody></table></div></>
      )}
    </section>
  );
}
